/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 25 2024
 **/

import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { Properties } from "@/frameworks/libraries/properties";
import { useChat } from "@/frameworks/core/API";
import { Configure } from "@/frameworks/libraries/utilities";
import { queryClientStatic } from "../../API/private";
import { RestAPIError } from "@/frameworks/libraries";
import { SystemError } from "../../commons";

export default function useRoomViewActions() {
	const chatAPI = useChat();

	const { refetch: getRoomView } = useQuery({
		queryKey: ["getRoomView"],
		queryFn: async () => {
			const result: Map<string, WebServices.Chat.RoomView.Data> = new Map();
			const { data } = await chatAPI.getRoomView.execute();
			if (data) {
				result.set(Configure.userID, data);
			}
			return result;
		},
		enabled: false
	});

	const updateRoomView = async ({
		roomId,
		deviceKey,
		view
	}: {
		roomId: string;
		deviceKey: string;
		view: boolean;
	}) => {
		try {
			await chatAPI.updateView.execute({ roomId, deviceKey, view });
		} catch (error) {
			const _error = error as RestAPIError;
			if (_error.name === "RestAPIError") {
				if (_error.code === "49402") {
					new SystemError(_error.message, {
						cause: {
							errorCode: _error.code,
							errorMessage: _error.message
						}
					});
				}
			}
		}
	};

	const isActiveRoom = useCallback((roomID: string) => {
		const roomViewState = queryClientStatic.getQueryData<Map<string, WebServices.Chat.RoomView.Data>>([
			"getRoomView"
		]);
		const state = roomViewState?.get(Configure.userID);
		if (state && state.findIndex((item) => item.roomId === roomID) !== -1) {
			return true;
		}

		return false;
	}, []);

	const roomViewCloseAll = useCallback(() => {
		const roomViewState = queryClientStatic.getQueryData<Map<string, WebServices.Chat.RoomView.Data>>([
			"getRoomView"
		]);
		const state = roomViewState?.get(Configure.userID);
		state?.forEach(async (room) => {
			if (room.deviceKey === Properties.browserTabID)
				return await updateRoomView({ roomId: room.roomId, deviceKey: room.deviceKey, view: false });
		});
	}, []);

	return {
		load: getRoomView,
		updateRoomView,
		isActiveRoom,
		roomViewCloseAll
	};
}
